import { dayDate, tripStart } from "./days.ts";
import type { When } from "./model.ts";

// What changing a trip's dates does to what's in it (features/trips-and-members.md, "The trip's name and dates"): the
// things on relative days ("day 3") count from its first day, so they move with it; things on dates stay where they
// are, some now outside the trip's dates. Said in a write's reply (`hints`), to an AI and on the web alike, never
// changed by Tripmo itself.

export interface DatesHint {
  kind: "relative_days" | "outside_dates";
  ids: string[];
  titles: string[];
  // Each one's first day: a date, or, on a relative day, "day 3" and the date it is now ("day 3, 2026-11-30"; just
  // "day 3" while the trip has no dates).
  days: string[];
  fix: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const RELATIVE = /^day ([1-9]\d{0,2})$/;
// Not in the trip's days any more: called off, dropped, or an option not chosen.
const GONE = new Set(["dropped", "cancelled", "not_chosen"]);

interface Thing {
  id: string;
  status: string;
  fields: { title: string; when?: When | undefined };
}

// A time's first and last day as written: dates, "day N", or a local time's date.
function ends(when: When | undefined): { first: string; last: string } | null {
  if (!when) return null;
  switch (when.precision) {
    case "none":
      return null;
    case "day":
      return { first: when.day, last: when.day };
    case "part":
      return when.day ? { first: when.day, last: when.day } : null;
    case "days":
      return { first: when.start, last: when.end };
    case "window":
    case "exact":
      return { first: when.start.slice(0, 10), last: (when.end ?? when.start).slice(0, 10) };
  }
}

// The trip's last date, when it has a first.
function tripEnd(dates: When | undefined): string | null {
  const start = tripStart(dates);
  if (!start || !dates) return null;
  return dates.precision === "days" && DATE.test(dates.end) ? dates.end : start;
}

export function datesHints(before: When | undefined, after: When | undefined, things: Thing[]): DatesHint[] {
  const out: DatesHint[] = [];
  const live = things.filter((t) => !GONE.has(t.status));
  const was = tripStart(before);
  const now = tripStart(after);
  // Relative days move with the trip's first day: said when that day changed, or came or went.
  if (was !== now) {
    const relative = live.flatMap((t) => {
      const e = ends(t.fields.when);
      return e && RELATIVE.test(e.first) ? [{ t, day: e.first }] : [];
    });
    if (relative.length > 0)
      out.push({
        kind: "relative_days",
        ids: relative.map((r) => r.t.id),
        titles: relative.map((r) => r.t.fields.title),
        days: relative.map(({ day }) => {
          const date = now ? dayDate(Number(day.slice(4)), now) : null;
          return date ? `${day}, ${date}` : day;
        }),
        fix: now
          ? `These are on relative days, which count from the trip's first day: day 1 is now ${now}. Check with your human that each is still on the day they meant, and give it a date if it was meant for one.`
          : "These are on relative days, which count from the trip's first day: with no dates now, they have no date either.",
      });
  }
  // Things on dates outside the trip's dates now.
  const first = now;
  const last = tripEnd(after);
  if (first && last) {
    const outside = live.flatMap((t) => {
      const e = ends(t.fields.when);
      if (!e || !DATE.test(e.first) || !DATE.test(e.last)) return [];
      return e.last < first || e.first > last ? [{ t, day: e.first }] : [];
    });
    if (outside.length > 0)
      out.push({
        kind: "outside_dates",
        ids: outside.map((o) => o.t.id),
        titles: outside.map((o) => o.t.fields.title),
        days: outside.map((o) => o.day),
        fix: `These are on dates outside the trip's (${first} to ${last}). Ask your human whether to move them, or whether the trip's dates are right; nothing moved by itself.`,
      });
  }
  return out;
}

// Whether two of a trip's dates are the same, whatever order their keys came in (Postgres keeps JSON's keys in an
// order of its own).
export function sameDates(a: When | null | undefined, b: When | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  const keys = Object.keys(a).sort();
  return (
    keys.join() === Object.keys(b).sort().join() &&
    keys.every((k) => (a as Record<string, unknown>)[k] === (b as Record<string, unknown>)[k])
  );
}
